import axios, { AxiosError } from 'axios';
import {
  postComplianceDocumentsUploadSession,
  postMarketingAssetsUploadSession,
} from '@/lib/api';
import type { UploadAssetDomain } from '@/lib/uploadAssetDomain';

export function isRetryableUploadError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return false;
  }

  if (axios.isCancel(error)) {
    return false;
  }

  if (!axios.isAxiosError(error)) {
    return true;
  }

  const axiosError = error as AxiosError;
  if (axiosError.code === 'ERR_CANCELED') {
    return false;
  }

  if (!axiosError.response) {
    return true;
  }

  const status = axiosError.response.status;
  return (
    status === 401 ||
    status === 403 ||
    status === 408 ||
    status >= 500
  );
}

export async function refreshComplianceUploadSession(
  fileId: number,
): Promise<string> {
  const { results } = await postComplianceDocumentsUploadSession({
    file_ids: [fileId],
  });
  const entry = results.find((r) => r.file_id === fileId);
  if (entry?.error) {
    throw new Error(entry.error);
  }
  if (!entry?.upload_url) {
    throw new Error('Upload URL missing after session refresh');
  }
  return entry.upload_url;
}

export async function refreshMarketingUploadSession(
  fileId: number,
): Promise<string> {
  const { results } = await postMarketingAssetsUploadSession({
    file_ids: [fileId],
  });
  const entry = results.find((r) => r.file_id === fileId);
  if (entry?.error) {
    throw new Error(entry.error);
  }
  if (!entry?.upload_url) {
    throw new Error('Upload URL missing after session refresh');
  }
  return entry.upload_url;
}

export async function refreshUploadSession(
  assetDomain: UploadAssetDomain,
  fileId: number,
): Promise<string> {
  if (assetDomain === 'marketing_assets') {
    return refreshMarketingUploadSession(fileId);
  }
  return refreshComplianceUploadSession(fileId);
}
